import { apiClient } from "./client";

async function get(path: string) {
  const { data } = await apiClient.get(path);
  return data;
}
async function post(path: string, body?: unknown) {
  const { data } = await apiClient.post(path, body ?? {});
  return data;
}
async function patch(path: string, body?: unknown) {
  const { data } = await apiClient.patch(path, body ?? {});
  return data;
}
async function put(path: string, body?: unknown) {
  const { data } = await apiClient.put(path, body ?? {});
  return data;
}
async function del(path: string) {
  const { data } = await apiClient.delete(path);
  return data;
}

/** Download a CSV/blob export and trigger a browser save. */
export async function downloadExport(path: string, filename: string) {
  const res = await apiClient.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Admin API surface — hits the NestJS backend at /v1/admin/* + /v1/auth/admin/*. */
export const adminApi = {
  async login(email: string, password: string) {
    const { data } = await apiClient.post("/v1/auth/admin/login", { email, password });
    return data as {
      success: boolean;
      access_token: string;
      refresh_token: string;
      admin: { id: string; name: string; role: string };
    };
  },

  // ── reads ──
  dashboardOverview: () => get("/v1/admin/dashboard/overview"),
  dashboardCountries: () => get("/v1/admin/dashboard/countries"),
  // ── visitor analytics (aggregates only; no visitor-level rows are exposed) ──
  visitorAnalytics: (from?: string, to?: string) =>
    get(`/v1/admin/analytics/overview?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`),
  visitorAnalyticsDaily: (from?: string, to?: string) =>
    get(`/v1/admin/analytics/daily?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`),
  /** Markets a merchant is enabled for, plus any wildcard rules that overlap. */
  merchantCountries: (id: string) => get(`/v1/admin/merchants/${id}/countries`),
  /** Enable a market: pins existing wildcards, then adds currency-scoped rules. */
  enableMerchantCountry: (id: string, code: string, body?: { include_payout?: boolean; pin_existing_to?: string }) =>
    post(`/v1/admin/merchants/${id}/countries/${code}`, body ?? {}),
  disableMerchantCountry: (id: string, code: string) => del(`/v1/admin/merchants/${id}/countries/${code}`),
  merchants: (qs = "") => get(`/v1/admin/merchants${qs}`),
  /** What is collectable from a merchant right now. */
  merchantBalance: (id: string) => get(`/v1/admin/merchants/${id}/balance`),
  merchant: (id: string) => get(`/v1/admin/merchants/${id}`),
  /**
   * Everything the merchant detail page shows, in ONE request.
   *
   * Six requests would mean six spinners and six chances to half-load, and
   * figures read at six different moments can disagree with each other on
   * screen. The endpoint reads them together.
   */
  merchantSummary: (id: string) => get(`/v1/admin/merchants/${id}/summary`),

  // ── country profiles ──
  /** All profiles, filterable by merchant, market and status. */
  countryProfiles: (qs = "") => get(`/v1/admin/country-profiles${qs}`),
  countryProfile: (merchantId: string, country: string) =>
    get(`/v1/admin/country-profiles/${merchantId}/${country}`),
  /** Routing decision against authorisation decision — gates enforcement. */
  countryProfileDiscrepancies: () => get("/v1/admin/country-profiles/discrepancies"),
  countryProfileAction: (
    merchantId: string,
    country: string,
    verb: "approve" | "reject" | "suspend",
    reason: string
  ) => post(`/v1/admin/country-profiles/${merchantId}/${country}/${verb}`, { reason }),
  /**
   * Per-market overrides. `null` CLEARS a field back to inheriting; an absent
   * field is untouched; 0 and false are values. Sent as-is so nothing coerces
   * a deliberate zero into an absence.
   */
  countryProfileUpdate: (merchantId: string, country: string, body: unknown) =>
    patch(`/v1/admin/country-profiles/${merchantId}/${country}`, body),
  providers: () => get("/v1/admin/providers"),
  provider: (id: string) => get(`/v1/admin/providers/${id}`),
  providerHealth: (id: string) => get(`/v1/admin/providers/${id}/health`),
  providerBalance: (id: string) => get(`/v1/admin/providers/${id}/balance`),
  /** What every provider is holding for us, per currency. */
  providerBalances: () => get("/v1/admin/providers/balances"),
  /** Live spendable balance per payout-capable provider (for the payout picker). */
  payoutBalances: (opts?: { refresh?: boolean; currency?: string }) =>
    get(`/v1/admin/providers/payout-balances?${new URLSearchParams({
      ...(opts?.refresh ? { refresh: "1" } : {}),
      ...(opts?.currency ? { currency: opts.currency } : {}),
    }).toString()}`),
  /** One provider's float split per merchant. */
  providerBalanceToCollect: (id: string) => get(`/v1/admin/providers/${id}/balance-to-collect`),
  routingRules: (qs = "") => get(`/v1/admin/routing${qs}`),
  routingStats: () => get("/v1/admin/routing/stats"),
  routingProviders: () => get("/v1/admin/routing/providers"),
  routingDistribution: () => get("/v1/admin/routing/distribution"),
  routingProviderHealth: () => get("/v1/admin/routing/provider-health"),
  payinOrders: (qs = "") => get(`/v1/admin/orders/payin${qs}`),
  payoutOrders: (qs = "") => get(`/v1/admin/orders/payout${qs}`),
  payinOrder: (id: string) => get(`/v1/admin/orders/payin/${id}`),
  payoutOrder: (id: string) => get(`/v1/admin/orders/payout/${id}`),
  settlements: (qs = "") => get(`/v1/admin/settlements${qs}`),
  auditLogs: (qs = "") => get(`/v1/admin/audit-logs${qs}`),
  webhookDeliveries: (qs = "") => get(`/v1/admin/webhooks/deliveries${qs}`),
  webhookDelivery: (id: string) => get(`/v1/admin/webhooks/deliveries/${id}`),
  providerWebhookLogs: (qs = "") => get(`/v1/admin/webhooks/provider-logs${qs}`),
  providerWebhookLog: (id: string) => get(`/v1/admin/webhooks/provider-logs/${id}`),
  failedWebhooks: (qs = "") => get(`/v1/admin/webhooks/failed${qs}`),
  internalAccounts: () => get("/v1/admin/internal-accounts"),
  systemConfig: () => get("/v1/admin/system-config"),
  aquapayWallets: () => get("/v1/admin/wallets/aquapay"),
  subscribers: (qs = "") => get(`/v1/admin/subscribers${qs}`),
  subscriber: (id: string) => get(`/v1/admin/subscribers/${id}`),

  // ── manual collection: collector accounts + incoming bank credits ──
  collectors: () => get("/v1/admin/collectors"),
  createCollector: (body: unknown) => post("/v1/admin/collectors", body),
  updateCollector: (id: string, body: unknown) => patch(`/v1/admin/collectors/${id}`, body),
  collectorOrders: (id: string) => get(`/v1/admin/collectors/${id}/orders`),
  collectorTransactions: (id: string, qs = "") => get(`/v1/admin/collectors/${id}/transactions${qs}`),
  collectorCredit: (id: string, body: unknown) => post(`/v1/admin/collectors/${id}/credit`, body),
  collectorStatement: (id: string, body: unknown) => post(`/v1/admin/collectors/${id}/statement`, body),
  collectorUsers: () => get("/v1/admin/collectors/users"),
  createCollectorUser: (body: unknown) => post("/v1/admin/collectors/users", body),
  collectorUserWallet: (id: string) => get(`/v1/admin/collectors/users/${id}/wallet`),
  collectorUserLedger: (id: string) => get(`/v1/admin/collectors/users/${id}/ledger`),
  /** Payer-raised disputes on the in-house rails; channel = BANK | UPI. */
  manualDisputes: (channel?: string, status = "ALL") =>
    get(`/v1/admin/collectors/disputes?status=${status}${channel ? `&channel=${channel}` : ""}`),
  manualDisputeCounts: () => get("/v1/admin/collectors/disputes/counts"),

  // ── collector prepaid-float wallets, USDT top-ups, rates, deposit pool ──
  cwOverview: () => get("/v1/admin/collector-wallets/overview"),
  cwWallets: (limit = 100) => get(`/v1/admin/collector-wallets?limit=${limit}`),
  cwLedger: (ownerAdminId?: string, limit = 100) =>
    get(`/v1/admin/collector-wallets/ledger?limit=${limit}${ownerAdminId ? `&owner_admin_id=${ownerAdminId}` : ""}`),
  cwTopups: (status = "ALL", limit = 100) => get(`/v1/admin/collector-wallets/topups?status=${status}&limit=${limit}`),
  cwAdjustments: (status = "ALL") => get(`/v1/admin/collector-wallets/adjustments?status=${status}`),
  cwAdjust: (body: unknown) => post("/v1/admin/collector-wallets/adjust", body),
  cwApprove: (id: string) => post(`/v1/admin/collector-wallets/adjustments/${id}/approve`),
  cwReject: (id: string, reason?: string) => post(`/v1/admin/collector-wallets/adjustments/${id}/reject`, { reason }),
  cwFreeze: (body: unknown) => post("/v1/admin/collector-wallets/freeze", body),
  cwRates: (includeInactive = false) => get(`/v1/admin/collector-wallets/rates?include_inactive=${includeInactive}`),
  cwCurrentRate: () => get("/v1/admin/collector-wallets/rates/current"),
  cwSetRate: (body: unknown) => post("/v1/admin/collector-wallets/rates", body),
  cwDeactivateRate: (id: string) => del(`/v1/admin/collector-wallets/rates/${id}`),
  cwDepositPool: () => get("/v1/admin/collector-wallets/deposit-pool"),
  cwAddPool: (body: unknown) => post("/v1/admin/collector-wallets/deposit-pool", body),
  cwSetDepositAddress: (body: unknown) => post("/v1/admin/collector-wallets/deposit-address", body),
  cwScanDeposits: () => post("/v1/admin/collector-wallets/scan-deposits"),
  cwVerifyDeposit: (body: unknown) => post("/v1/admin/collector-wallets/verify-deposit", body),
  cwConfig: () => get("/v1/admin/collector-wallets/config"),
  cwSetConfig: (body: unknown) => post("/v1/admin/collector-wallets/config", body),
  cwRecon: () => get("/v1/admin/collector-wallets/reconciliation"),
  cwReconRuns: (limit = 20) => get(`/v1/admin/collector-wallets/reconciliation/runs?limit=${limit}`),
  cwReconIssues: (resolved?: boolean, kind = "ALL") =>
    get(`/v1/admin/collector-wallets/reconciliation/discrepancies?kind=${kind}${resolved === undefined ? "" : `&resolved=${resolved}`}`),
  cwReconRun: () => post("/v1/admin/collector-wallets/reconciliation/run"),
  cwReconResolve: (id: string, note?: string) =>
    post(`/v1/admin/collector-wallets/reconciliation/discrepancies/${id}/resolve`, { note }),
  recordCollectorLedger: (id: string, body: unknown) => post(`/v1/admin/collectors/users/${id}/ledger`, body),

  // ── subscribers (white-label) ──
  createSubscriber: (body: unknown) => post("/v1/admin/subscribers", body),
  updateSubscriber: (id: string, body: unknown) => patch(`/v1/admin/subscribers/${id}`, body),
  approveSubscriber: (id: string) => post(`/v1/admin/subscribers/${id}/approve`),
  suspendSubscriber: (id: string) => post(`/v1/admin/subscribers/${id}/suspend`),
  terminateSubscriber: (id: string) => post(`/v1/admin/subscribers/${id}/terminate`),

  // ── plans ──
  plans: (qs = "") => get(`/v1/admin/plans${qs}`),
  plan: (id: string) => get(`/v1/admin/plans/${id}`),
  createPlan: (body: unknown) => post("/v1/admin/plans", body),
  updatePlan: (id: string, body: unknown) => patch(`/v1/admin/plans/${id}`, body),
  deactivatePlan: (id: string) => del(`/v1/admin/plans/${id}`),

  // ── subscriptions ──
  assignSubscription: (body: unknown) => post("/v1/admin/subscriptions", body),
  subscription: (subscriberId: string) => get(`/v1/admin/subscriptions/${subscriberId}`),
  updateSubscription: (subscriberId: string, body: unknown) => patch(`/v1/admin/subscriptions/${subscriberId}`, body),

  // ── invoices ──
  invoices: (qs = "") => get(`/v1/admin/invoices${qs}`),
  invoice: (id: string) => get(`/v1/admin/invoices/${id}`),
  generateInvoice: (body: unknown) => post("/v1/admin/invoices/generate", body),
  payInvoice: (id: string) => post(`/v1/admin/invoices/${id}/pay`),
  voidInvoice: (id: string) => post(`/v1/admin/invoices/${id}/void`),

  // ── commission ──
  commissionLedger: (qs = "") => get(`/v1/admin/commission${qs}`),
  commissionSummary: (qs = "") => get(`/v1/admin/commission/summary${qs}`),

  // ── platform analytics ──
  platformRevenue: () => get("/v1/admin/platform/revenue"),
  platformSubscribers: () => get("/v1/admin/platform/subscribers-summary"),
  platformTimeseries: (range: string) => get(`/v1/admin/platform/timeseries?range=${range}`),

  // ── tenant branding ──
  tenantBranding: (tenantId: string) => get(`/v1/admin/tenants/${tenantId}/branding`),
  saveTenantBranding: (tenantId: string, body: unknown) => put(`/v1/admin/tenants/${tenantId}/branding`, body),

  // ── merchants ──
  createMerchant: (body: unknown) => post("/v1/admin/merchants", body),
  updateMerchant: (id: string, body: unknown) => patch(`/v1/admin/merchants/${id}`, body),
  deleteMerchant: (id: string) => del(`/v1/admin/merchants/${id}`),
  regenerateCredentials: (id: string) => post(`/v1/admin/merchants/${id}/regenerate-credentials`),
  setMerchantPassword: (id: string, password?: string) => post(`/v1/admin/merchants/${id}/set-password`, password ? { password } : {}),
  resetMerchant2fa: (id: string) => post(`/v1/admin/merchants/${id}/reset-2fa`),

  // ── providers ──
  createProvider: (body: unknown) => post("/v1/admin/providers", body),
  updateProvider: (id: string, body: unknown) => patch(`/v1/admin/providers/${id}`, body),
  testProviderConnection: (id: string) => post(`/v1/admin/providers/${id}/test-connection`),
  providerAdmins: (id: string) => get(`/v1/admin/providers/${id}/admins`),
  createProviderAdmin: (id: string, body: unknown) => post(`/v1/admin/providers/${id}/admins`, body),

  // ── routing ──
  routingMetrics: () => get("/v1/admin/routing/metrics"),
  toggleRouting: (enabled: boolean) => post("/v1/admin/routing/toggle", { enabled }),
  createRoutingRule: (body: unknown) => post("/v1/admin/routing", body),
  updateRoutingRule: (id: string, body: unknown) => patch(`/v1/admin/routing/${id}`, body),
  deleteRoutingRule: (id: string) => del(`/v1/admin/routing/${id}`),
  setProviderPipeline: (providerId: string, body: unknown) =>
    patch(`/v1/admin/routing/providers/${providerId}/pipeline`, body),

  // ── orders ──
  overridePayin: (id: string, body: unknown) =>
    post(`/v1/admin/orders/payin/${id}/override-status`, body),
  editPayinAmount: (id: string, body: unknown) =>
    patch(`/v1/admin/orders/payin/${id}/amount`, body),
  refundPayin: (id: string, body: unknown) =>
    post(`/v1/admin/orders/payin/${id}/refund`, body),

  // ── withdrawals (merchant balance payouts) ──
  withdrawals: (qs = "") => get(`/v1/admin/withdrawals${qs}`),
  approveWithdrawal: (id: string, body?: unknown) => post(`/v1/admin/withdrawals/${id}/approve`, body ?? {}),
  rejectWithdrawal: (id: string, body?: unknown) => post(`/v1/admin/withdrawals/${id}/reject`, body ?? {}),
  markWithdrawalPaid: (id: string, body?: unknown) => post(`/v1/admin/withdrawals/${id}/mark-paid`, body ?? {}),
  overridePayout: (id: string, body: unknown) =>
    post(`/v1/admin/orders/payout/${id}/override-status`, body),
  refreshPayinStatus: (id: string) => post(`/v1/admin/orders/payin/${id}/refresh-status`),
  resendPayinWebhook: (id: string) => post(`/v1/admin/orders/payin/${id}/resend-webhook`),
  resendPayoutWebhook: (id: string) => post(`/v1/admin/orders/payout/${id}/resend-webhook`),
  syncPending: () => post("/v1/admin/orders/sync-pending"),

  // ── payouts (admin) ──
  createPayout: (body: unknown) => post("/v1/admin/payouts/create", body),
  approvePayout: (id: string) => post(`/v1/admin/payouts/${id}/approve`),
  rejectPayout: (id: string, body?: unknown) => post(`/v1/admin/payouts/${id}/reject`, body),
  reprocessPayout: (id: string) => post(`/v1/admin/payouts/${id}/reprocess`),

  // ── payment links ──
  createPaymentLink: (body: unknown) => post("/v1/admin/payment-links/create", body),

  // ── webhooks ──
  replayWebhook: (id: string) => post(`/v1/admin/webhooks/deliveries/${id}/replay`),

  // ── settlements ──
  settlementOverview: () => get("/v1/admin/settlements/overview"),
  generateSettlement: (body: unknown) => post("/v1/admin/settlements/generate", body),
  approveSettlement: (id: string) => post(`/v1/admin/settlements/${id}/approve`),
  executeSettlement: (id: string) => post(`/v1/admin/settlements/${id}/execute`),
  reconcileSettlements: () => get("/v1/admin/settlements/reconcile"),

  // ── system config ──
  setUsdtRate: (rate: number) => put("/v1/admin/system-config/usdt-rate", { rate }),
  setTelegramAdminIds: (ids: string) => put("/v1/admin/system-config/telegram-admin-ids", { ids }),

  // ── internal accounts ──
  createInternalAccount: (body: unknown) => post("/v1/admin/internal-accounts", body),
  updateInternalAccount: (id: string, body: unknown) =>
    patch(`/v1/admin/internal-accounts/${id}`, body),
  deleteInternalAccount: (id: string) => del(`/v1/admin/internal-accounts/${id}`),

  // ── wallets / treasury (module mounted in backend) ──
  topupAquapayWallet: (body: unknown) => post("/v1/admin/wallets/aquapay/topup", body),
  topupMerchantWallet: (merchantId: string, body: unknown) =>
    post(`/v1/admin/wallets/merchants/${merchantId}/topup`, body),

  // ── disputes ──
  disputes: (qs = "") => get(`/v1/admin/disputes${qs}`),
  dispute: (id: string) => get(`/v1/admin/disputes/${id}`),
  disputeStats: () => get("/v1/admin/disputes/stats"),
  disputeMessage: (id: string, body: unknown) => post(`/v1/admin/disputes/${id}/messages`, body),
  disputeStatus: (id: string, body: unknown) => post(`/v1/admin/disputes/${id}/status`, body),
  disputeEscalate: (id: string, body: unknown) => post(`/v1/admin/disputes/${id}/escalate`, body),
  disputeAssign: (id: string, body: unknown) => post(`/v1/admin/disputes/${id}/assign`, body),
  disputeMerge: (id: string, body: unknown) => post(`/v1/admin/disputes/${id}/merge`, body),
  disputeAttachmentUrl: (id: string, attId: string) => get(`/v1/admin/disputes/${id}/attachments/${attId}/url`),

  // ── support tickets ──
  tickets: (qs = "") => get(`/v1/admin/tickets${qs}`),
  ticket: (id: string) => get(`/v1/admin/tickets/${id}`),
  ticketReply: (id: string, body: unknown) => post(`/v1/admin/tickets/${id}/messages`, body),
  ticketStatus: (id: string, body: unknown) => post(`/v1/admin/tickets/${id}/status`, body),
  ticketAssign: (id: string, body: unknown) => post(`/v1/admin/tickets/${id}/assign`, body),

  // ── notifications (bell) ──
  notifications: (unread = false) => get(`/v1/admin/notifications${unread ? "?unread=1" : ""}`),
  notificationRead: (id: string) => post(`/v1/admin/notifications/${id}/read`),
  notificationReadAll: () => post("/v1/admin/notifications/read-all"),
};
